import type {
  CellProps,
  ColumnData,
  ColumnDataSchemaModel,
  ColumnRegular,
  ReadOnlyFormat,
} from '@revolist/react-datagrid';

/** Applied to any cell that resolves as readonly, whether via the grid-level `readonly` prop or a column's own `readonly`. */
export const READONLY_CELL_CLASS = 'readonly-cell';

export const resolveReadonly = (
  readonly: ReadOnlyFormat | undefined,
  props: ColumnDataSchemaModel
): boolean => (typeof readonly === 'function' ? readonly(props) : !!readonly);

const withReadonlyClass = (base: CellProps['class'], isReadonly: boolean): CellProps['class'] => {
  const baseClasses = typeof base === 'string' ? { [base]: true } : base ?? {};
  return { ...baseClasses, [READONLY_CELL_CLASS]: isReadonly };
};

const withReadonlyCell = (column: ColumnRegular, gridReadonly: boolean): ColumnRegular => {
  const { cellProperties } = column;
  return {
    ...column,
    cellProperties: (props): CellProps => {
      const isReadonly = resolveReadonly(column.readonly, props);
      const base: CellProps = cellProperties?.(props) ?? {};
      base.class = withReadonlyClass(base.class, isReadonly);
      return base;
    },
  };
};

/** Gives every readonly cell the `READONLY_CELL_CLASS` */
export const withReadonlyCellStyling = (columns: ColumnData, gridReadonly: boolean): ColumnData =>
  columns.map((column) =>
    'children' in column
      ? { ...column, children: withReadonlyCellStyling(column.children, gridReadonly) }
      : withReadonlyCell(column, gridReadonly)
  );
